import { AppNotification, CompanyConfig, NotificationEventType, GlobalUser, Profile } from '../types';
import { supabase } from '../supabaseClient';

export interface NotificationEventDefinition {
    key: NotificationEventType;
    name: string;
    description: string;
    category: 'Planificación' | 'Calidad';
    defaultTitle: string;
    defaultTemplate: string;
    allowedVariables: string[];
    icon: string;
    type: 'planning' | 'quality' | 'system';
    requiresAttention: boolean;
}

export const NOTIFICATION_EVENT_DEFINITIONS: Record<string, NotificationEventDefinition> = {
    task_assigned: {
        key: 'task_assigned',
        name: 'Asignación de tarea',
        description: 'Notifica al responsable cuando se le asigne una tarea en la obra.',
        category: 'Planificación',
        defaultTitle: 'Nueva Tarea Asignada',
        defaultTemplate: 'Hola {usuario}, se te ha asignado la tarea "{tarea}" en la obra {obra}.',
        allowedVariables: ['{usuario}', '{obra}', '{tarea}', '{planificacion}'],
        icon: 'assignment_ind',
        type: 'planning',
        requiresAttention: false
    },
    review_requested: {
        key: 'review_requested',
        name: 'Solicitud de revisión',
        description: 'Notifica al revisor o aprobador correspondiente cuando una tarea o planificación quede disponible para su revisión.',
        category: 'Planificación',
        defaultTitle: 'Solicitud de Revisión Pendiente',
        defaultTemplate: 'La tarea "{tarea}" en {obra} ha quedado disponible para tu revisión.',
        allowedVariables: ['{usuario}', '{obra}', '{tarea}', '{planificacion}'],
        icon: 'rate_review',
        type: 'planning',
        requiresAttention: true
    },
    protocol_released: {
        key: 'protocol_released',
        name: 'Liberación de protocolo',
        description: 'Notifica al siguiente revisor cuando corresponda continuar el flujo de calidad.',
        category: 'Calidad',
        defaultTitle: 'Liberación de Protocolo',
        defaultTemplate: 'Se ha liberado una etapa del protocolo "{protocolo}" en {obra}. Corresponde tu revisión.',
        allowedVariables: ['{usuario}', '{obra}', '{protocolo}', '{tarea}'],
        icon: 'verified',
        type: 'quality',
        requiresAttention: true
    },
    planning_published: {
        key: 'planning_published',
        name: 'Publicación de planificación',
        description: 'Notifica a los usuarios involucrados de la obra cuando una planificación pase a «En Ejecución».',
        category: 'Planificación',
        defaultTitle: 'Planificación en Ejecución',
        defaultTemplate: 'La planificación "{planificacion}" en la obra {obra} ha pasado a En Ejecución.',
        allowedVariables: ['{obra}', '{planificacion}'],
        icon: 'published_with_changes',
        type: 'planning',
        requiresAttention: false
    },
    review_rejected: {
        key: 'review_rejected',
        name: 'Revisión rechazada',
        description: 'Notifica al responsable correspondiente cuando una revisión sea rechazada y requiera corrección.',
        category: 'Planificación',
        defaultTitle: 'Revisión Rechazada',
        defaultTemplate: 'La revisión de "{tarea}" en la obra {obra} ha sido rechazada y requiere corrección.',
        allowedVariables: ['{usuario}', '{obra}', '{tarea}', '{planificacion}', '{protocolo}'],
        icon: 'cancel',
        type: 'planning',
        requiresAttention: true
    }
};

/**
 * Normalizes legacy event type strings to the core 5 event keys
 */
export function normalizeEventType(eventType: NotificationEventType): string {
    switch (eventType) {
        case 'task_created_assigned':
            return 'task_assigned';
        case 'protocol_released_next':
            return 'protocol_released';
        case 'planning_created_assigned':
            return 'planning_published';
        default:
            return eventType;
    }
}

/**
 * Replaces placeholders like {usuario}, {obra}, {tarea}, {planificacion}, {protocolo}
 */
export function formatNotificationMessage(
    template: string,
    variables?: {
        usuario?: string;
        obra?: string;
        tarea?: string;
        planificacion?: string;
        protocolo?: string;
        [key: string]: string | undefined;
    }
): string {
    if (!template) return '';
    let result = template;
    const vars = variables || {};

    result = result.replace(/\{usuario\}/g, vars.usuario || 'Usuario');
    result = result.replace(/\{obra\}/g, vars.obra || 'Obra');
    result = result.replace(/\{tarea\}/g, vars.tarea || 'Tarea');
    result = result.replace(/\{planificacion\}/g, vars.planificacion || 'Planificación');
    result = result.replace(/\{protocolo\}/g, vars.protocolo || 'Protocolo');

    return result;
}

export interface TriggerNotificationParams {
    eventType: NotificationEventType;
    title?: string;
    description?: string;
    type?: 'planning' | 'quality' | 'system' | 'payments' | 'prevention';
    link?: string;
    projectId?: string;
    projectName?: string;
    targetUserIds?: string[];
    responsibleId?: string;
    variables?: {
        usuario?: string;
        obra?: string;
        tarea?: string;
        planificacion?: string;
        protocolo?: string;
        [key: string]: string | undefined;
    };
    requiresAttention?: boolean;
    companyConfig?: CompanyConfig;
    dedupKey?: string;
    metadata?: Record<string, any>;
}

// In-memory cache for deduplication (prevent duplicate notifications fired within 10 seconds)
const recentNotifications = new Map<string, number>();

/**
 * Checks if a specific notification event type is enabled in company settings.
 * Defaults to true if unconfigured.
 */
export function isNotificationEnabled(
    eventType: NotificationEventType,
    companyConfig?: CompanyConfig
): boolean {
    if (!companyConfig || !companyConfig.notificationSettings) {
        return true;
    }

    const normKey = normalizeEventType(eventType);
    const settings = companyConfig.notificationSettings as any;

    // 1. Check object-based config { enabled: boolean }
    if (settings[normKey] && typeof settings[normKey].enabled === 'boolean') {
        return settings[normKey].enabled;
    }

    // 2. Check legacy boolean flags
    const legacyKey = `notify_${eventType}`;
    if (typeof settings[legacyKey] === 'boolean') {
        return settings[legacyKey];
    }
    const normLegacyKey = `notify_${normKey}`;
    if (typeof settings[normLegacyKey] === 'boolean') {
        return settings[normLegacyKey];
    }

    return true; // Enabled by default
}

/**
 * Resolves automatic recipients strictly respecting project membership and permissions.
 * Never broadcasts indiscriminately across obras.
 */
export function resolveRecipientsForEvent(params: {
    eventType: NotificationEventType;
    projectId?: string;
    targetUserIds?: string[];
    responsibleId?: string;
    globalUsers?: GlobalUser[];
    globalProfiles?: Profile[];
}): string[] {
    const { eventType, projectId, targetUserIds, responsibleId, globalUsers = [] } = params;
    const normKey = normalizeEventType(eventType);

    // If explicit targetUserIds are provided, validate they belong to the project (or are admin)
    if (targetUserIds && targetUserIds.length > 0) {
        if (!projectId) return Array.from(new Set(targetUserIds));
        return Array.from(new Set(
            targetUserIds.filter(userId => {
                if (userId === 'all') return true;
                const user = globalUsers.find(u => u.id === userId || u.email === userId);
                if (!user) return true; // Keep if user record not present in memory
                if (user.globalRole === 'Administrador') return true;
                return user.assignedProjects.some(ap => ap.projectId === projectId);
            })
        ));
    }

    if (!projectId) {
        // Without project context and no explicit targets, do not send indiscriminately
        return responsibleId ? [responsibleId] : [];
    }

    // Filter users belonging to this project
    const projectMembers = globalUsers.filter(u =>
        u.globalRole === 'Administrador' ||
        u.assignedProjects.some(ap => ap.projectId === projectId)
    );

    if (normKey === 'task_assigned' || normKey === 'review_rejected') {
        if (responsibleId) {
            const matched = projectMembers.find(u => u.id === responsibleId || u.email === responsibleId);
            return matched ? [matched.id] : [];
        }
        return [];
    }

    if (normKey === 'planning_published') {
        // Notify all active members of this project
        return projectMembers.map(u => u.id);
    }

    if (normKey === 'review_requested') {
        // Notify members with review capabilities in this project
        const reviewers = projectMembers.filter(u => {
            if (u.globalRole === 'Administrador') return true;
            const assignment = u.assignedProjects.find(ap => ap.projectId === projectId);
            const profName = (assignment?.profileName || assignment?.profileId || '').toLowerCase();
            return (
                profName.includes('oficina') ||
                profName.includes('administrador') ||
                profName.includes('jefe') ||
                profName.includes('calidad')
            );
        });
        return reviewers.map(u => u.id);
    }

    if (normKey === 'protocol_released') {
        if (responsibleId) {
            const matched = projectMembers.find(u => u.id === responsibleId || u.email === responsibleId);
            return matched ? [matched.id] : [];
        }
        return [];
    }

    return [];
}

/**
 * Centralized dispatcher for application notifications.
 * Non-blocking: will never crash or interrupt business operations.
 */
export function triggerAppNotification(params: TriggerNotificationParams): AppNotification[] {
    try {
        const {
            eventType,
            companyConfig,
            projectId,
            projectName,
            targetUserIds,
            variables,
            dedupKey,
            link,
            metadata
        } = params;

        // 1. Verify business toggle
        if (!isNotificationEnabled(eventType, companyConfig)) {
            return [];
        }

        const normKey = normalizeEventType(eventType);
        const eventDef = NOTIFICATION_EVENT_DEFINITIONS[normKey] || {
            key: eventType,
            name: eventType,
            description: '',
            category: 'Planificación' as const,
            defaultTitle: 'Notificación de Obra',
            defaultTemplate: '{tarea} en {obra}',
            allowedVariables: ['{usuario}', '{obra}', '{tarea}'],
            icon: 'notifications',
            type: (params.type || 'planning') as 'planning',
            requiresAttention: false
        };

        // 2. Resolve template & text
        const configuredSetting = companyConfig?.notificationSettings?.[normKey as keyof typeof companyConfig.notificationSettings] as any;
        const template = (configuredSetting && typeof configuredSetting.template === 'string' && configuredSetting.template.trim())
            ? configuredSetting.template
            : eventDef.defaultTemplate;

        const effectiveVariables = {
            obra: projectName || 'Obra',
            ...variables
        };

        const finalTitle = params.title || eventDef.defaultTitle;
        const finalDescription = params.description
            ? formatNotificationMessage(params.description, effectiveVariables)
            : formatNotificationMessage(template, effectiveVariables);

        const finalType = params.type || eventDef.type;
        const finalRequiresAttention = params.requiresAttention !== undefined
            ? params.requiresAttention
            : eventDef.requiresAttention;

        // 3. Deduplication check
        const targetKey = targetUserIds?.join(',') || 'all';
        const key = dedupKey || `${normKey}_${projectId || ''}_${finalTitle}_${targetKey}`;
        const now = Date.now();
        const lastTime = recentNotifications.get(key);
        if (lastTime && now - lastTime < 10000) {
            return []; // Suppress duplicate within 10-second threshold
        }
        recentNotifications.set(key, now);

        // Prune deduplication cache
        if (recentNotifications.size > 100) {
            for (const [k, time] of recentNotifications.entries()) {
                if (now - time > 60000) recentNotifications.delete(k);
            }
        }

        // 4. Create AppNotification objects
        const recipientList = targetUserIds && targetUserIds.length > 0 ? targetUserIds : ['all'];
        const newNotifications: AppNotification[] = recipientList.map((uId, idx) => ({
            id: `notif-${now}-${idx}-${Math.random().toString(36).substring(2, 8)}`,
            userId: uId,
            title: finalTitle,
            description: finalDescription,
            date: new Date().toISOString(),
            isRead: false,
            type: finalType,
            link,
            projectId,
            projectName,
            requiresAttention: finalRequiresAttention,
            eventType,
            metadata
        }));

        // 5. Broadcast in-app via window event for instant UI feedback
        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent('klyd_app_notification_created', {
                    detail: newNotifications
                })
            );
        }

        // 6. Secure Database Dispatch via RPC with server-side authorization and individual recipient insertion
        (async () => {
            try {
                const { data, error } = await supabase.rpc('create_operational_notification', {
                    p_event_type: normKey,
                    p_project_id: projectId || null,
                    p_project_name: projectName || null,
                    p_title: finalTitle,
                    p_description: finalDescription,
                    p_link: link || null,
                    p_entity_type: metadata?.entityType || null,
                    p_entity_id: metadata?.entityId || null,
                    p_category: finalType,
                    p_requires_attention: finalRequiresAttention,
                    p_metadata: metadata || {},
                    p_target_recipient_ids: targetUserIds && targetUserIds.length > 0 ? targetUserIds : null,
                    p_idempotency_key: dedupKey || null
                });

                if (error) {
                    // Informational logging only if function is not yet migrated in database
                    if (error.code !== 'PGRST205' && error.code !== '42883') {
                        console.info('[NotificationService] RPC notice:', error.message);
                    }
                } else if (data) {
                    console.log('[NotificationService] Notifications persisted securely:', data);
                }
            } catch (rpcErr: any) {
                // Non-blocking: background dispatch failure must never disrupt business operations
                console.info('[NotificationService] Background RPC notice:', rpcErr?.message || rpcErr);
            }
        })();

        return newNotifications;
    } catch (err) {
        // Gold Rule 8: Non-blocking support mechanism, never crash the host operation
        console.error('[NotificationService] Error in triggerAppNotification:', err);
        return [];
    }
}
